import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import localeTr from '@angular/common/locales/tr';
import { inject, Injectable, signal } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { map } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { User } from './models/user.model';
import { Match } from './models/match.model';
import { Category } from './models/category.model';
import { Tournament } from './models/tournament.model';
import { Sport } from './models/sport.model';
import { AppLanguageCode, normalizeAppLanguage } from './i18n/language.config';
import type { BettingSettings } from './betting-settings.service';

type ThemeMode = 'light' | 'dark';
const MOBILE_LAYOUT_MEDIA_QUERY = '(max-width: 1024px)';

let localeDataRegistered = false;

registerAppLocaleData();

@Injectable({ providedIn: 'root' })
export class SessionService {
  currentTheme = signal<ThemeMode>(this.resolveInitialTheme());
  currentLanguage = signal<AppLanguageCode>('en');
  currentUser = signal<User | null>(null);
  bettingSettings = signal<BettingSettings | null>(null);
  matches = signal(new Map<number, Match>());
  matchesLoading = signal(true);
  matchesLoadFailed = signal(false);
  categories = signal(new Map<number, Category>());
  tournaments = signal(new Map<number, Tournament>());
  sports = signal(new Map<number, string>());
  tournamentsBySport = signal(new Map<number, Set<number>>());
  matchesByTournament = signal(new Map<number, Set<number>>());
  selectedSports = signal(new Set<number>());
  private breakpointObserver: BreakpointObserver = inject(BreakpointObserver);

  readonly isMobile = toSignal(
    this.breakpointObserver.observe([MOBILE_LAYOUT_MEDIA_QUERY]).pipe(map(result => result.matches)),
    { initialValue: false }
  );
  menuItems = [
    'menu.myTickets',
    'menu.transactions',
    'menu.results',
    'menu.deposit',
    'menu.withdraw',
    'menu.settings'
  ];

  constructor() {
    this.applyTheme(this.currentTheme());
    const stored = sessionStorage.getItem('user');
    if (stored) {
      try {
        const user = JSON.parse(stored) as User;
        this.currentUser.set(user);
      } catch {
        sessionStorage.removeItem('user');
      }
    }
  }

  toggleTheme(): void {
    this.setTheme(this.currentTheme() === 'light' ? 'dark' : 'light');
  }

  setTheme(theme: ThemeMode): void {
    if (this.currentTheme() === theme) {
      return;
    }

    this.currentTheme.set(theme);
    this.applyTheme(theme);
  }

  setLanguage(language: string): void {
    const normalizedLanguage = normalizeAppLanguage(language);

    if (this.currentLanguage() === normalizedLanguage) {
      return;
    }

    this.currentLanguage.set(normalizedLanguage);
  }

  setBettingSettings(settings: BettingSettings): void {
    this.bettingSettings.set(settings);
  }

  setMatchesLoading(isLoading: boolean): void {
    if (this.matchesLoading() === isLoading) {
      return;
    }
    this.matchesLoading.set(isLoading);
  }

  resetLiveData(): void {
    this.matches.set(new Map());
    this.categories.set(new Map());
    this.tournaments.set(new Map());
    this.sports.set(new Map());
    this.tournamentsBySport.set(new Map());
    this.matchesByTournament.set(new Map());
    this.selectedSports.set(new Set());
  }

  addOrUpdateMatch(match: Match): void {
    const existing = this.matches().get(match.Id);
    this.matches.update(current => {
      const next = new Map(current);
      next.set(match.Id, match);
      return next;
    });
    if (existing) {
      return;
    }
    this.matchesByTournament.update(current => {
      const next = new Map(current);
      const matchesForTournament = new Set(next.get(match.LB_TournamentID) ?? []);
      matchesForTournament.add(match.Id);
      next.set(match.LB_TournamentID, matchesForTournament);
      return next;
    });
  }

  removeMatch(matchId: number): void {
    const existing = this.matches().get(matchId);
    if (!existing) {
      return;
    }
    this.matches.update(current => {
      const next = new Map(current);
      next.delete(matchId);
      return next;
    });
    this.matchesByTournament.update(current => {
      const next = new Map(current);
      const matchesForTournament = next.get(existing.LB_TournamentID);
      if (matchesForTournament) {
        const updated = new Set(matchesForTournament);
        updated.delete(matchId);
        if (updated.size) {
          next.set(existing.LB_TournamentID, updated);
        } else {
          next.delete(existing.LB_TournamentID);
        }
      }
      return next;
    });
  }

  updateMatch(matchId: number, updater: (match: Match) => Match): void {
    this.matches.update(current => {
      const existing = current.get(matchId);
      if (!existing) {
        return current;
      }

      const next = new Map(current);
      next.set(matchId, updater(existing));
      return next;
    });
  }

  removeBetTypes(betTypeIds: readonly number[]): void {
    if (!betTypeIds.length) {
      return;
    }

    const betTypeIdSet = new Set(betTypeIds);
    this.matches.update(current => {
      let hasChanges = false;
      const next = new Map(current);

      for (const [matchId, match] of current) {
        const filteredBetTypes = match.BetTypes.filter(betType => !betTypeIdSet.has(betType.Id));
        if (filteredBetTypes.length === match.BetTypes.length) {
          continue;
        }

        hasChanges = true;
        next.set(matchId, {
          ...match,
          BetTypes: filteredBetTypes
        });
      }

      return hasChanges ? next : current;
    });
  }

  addSport(sportId: number): void {
    this.sports.update(current => {
      if (current.has(sportId)) {
        return current;
      }
      const next = new Map(current);
      next.set(sportId, Sport[sportId as Sport] as string);
      return next;
    });
  }

  addCategory(category: Category): void {
    const existing = this.categories().get(category.LB_CategoryID);
    if (existing && areCategoriesEqual(existing, category)) {
      return;
    }

    this.categories.update(current => {
      const next = new Map(current);
      next.set(category.LB_CategoryID, category);
      return next;
    });
  }

  addTournament(tournament: Tournament): void {
    const tournamentId = tournament.LB_TournamentID;
    const existing = this.tournaments().get(tournamentId);
    if (existing && areTournamentsEqual(existing, tournament)) {
      return;
    }

    this.tournaments.update(current => {
      const next = new Map(current);
      next.set(tournamentId, tournament);
      return next;
    });
    this.tournamentsBySport.update(current => {
      if (existing && existing.Sport === tournament.Sport) {
        return current;
      }

      const next = new Map(current);

      if (existing) {
        const previousSportTournaments = next.get(existing.Sport);
        if (previousSportTournaments) {
          const updated = new Set(previousSportTournaments);
          updated.delete(tournamentId);
          if (updated.size) {
            next.set(existing.Sport, updated);
          } else {
            next.delete(existing.Sport);
          }
        }
      }

      const tournamentsForSport = new Set(next.get(tournament.Sport) ?? []);
      tournamentsForSport.add(tournamentId);
      next.set(tournament.Sport, tournamentsForSport);
      return next;
    });
  }

  toggleSportSelection(sportId: number): void {
    this.selectedSports.update(current => {
      const next = new Set(current);
      if (next.has(sportId)) {
        next.delete(sportId);
      } else {
        next.add(sportId);
      }
      return next;
    });
  }

  isSportSelected(sportId: number): boolean {
    return this.selectedSports().has(sportId);
  }

  setUser(user: User | null): void {
    this.currentUser.set(user);
    if (user) {
      sessionStorage.setItem('user', JSON.stringify(user));
      return;
    }

    sessionStorage.removeItem('user');
  }

  private resolveInitialTheme(): ThemeMode {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    return prefersDark ? 'dark' : 'light';
  }

  private applyTheme(theme: ThemeMode): void {
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.style.colorScheme = theme;
    document.body.setAttribute('data-theme', theme);
  }
}

function registerAppLocaleData(): void {
  if (localeDataRegistered) {
    return;
  }

  registerLocaleData(localeEn);
  registerLocaleData(localeDe);
  registerLocaleData(localeTr);
  localeDataRegistered = true;
}

function areCategoriesEqual(left: Category, right: Category): boolean {
  return (
    left.LB_CategoryID === right.LB_CategoryID &&
    left.LB_Category === right.LB_Category &&
    left.LB_CategorySortIndex === right.LB_CategorySortIndex &&
    left.Category === right.Category &&
    left.CountryCode === right.CountryCode &&
    left.Sport === right.Sport
  );
}

function areTournamentsEqual(left: Tournament, right: Tournament): boolean {
  return (
    left.LB_TournamentID === right.LB_TournamentID &&
    left.LB_TournamentSortIndex === right.LB_TournamentSortIndex &&
    left.Tournament === right.Tournament &&
    left.BrTournamentID === right.BrTournamentID &&
    left.TournamentRating === right.TournamentRating &&
    left.EndDate === right.EndDate &&
    left.Sport === right.Sport
  );
}
